import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { ICON_NAMES, getServiceIcon } from "../services/ui/iconMap";
import { getCategoryName } from "../services/utils/getCategoryName";
import type { AdminCategory } from "./ServiceCategoriesTab";

export const NO_PARENT = "__none__";

export interface CategoryFieldsValue {
  key: string;
  nameEt: string;
  nameEn: string;
  nameRu: string;
  icon: string;
  parentId: string; // NO_PARENT or a top-level category id
  sortOrder: string;
}

interface Props {
  value: CategoryFieldsValue;
  onChange: (patch: Partial<CategoryFieldsValue>) => void;
  keyEditable: boolean;
  topLevel: AdminCategory[];
  /** Exclude this id from the parent options (a category can't parent itself). */
  excludeId?: string;
}

export function CategoryFields({ value, onChange, keyEditable, topLevel, excludeId }: Props) {
  const { t, i18n } = useTranslation();
  const parentOptions = topLevel.filter((c) => c.id !== excludeId);

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-1">
        <Label>{t("admin.categories.fieldKey")}</Label>
        <Input
          value={value.key}
          onChange={(e) => onChange({ key: e.target.value })}
          disabled={!keyEditable}
          placeholder="repair_glass"
          className="font-mono"
        />
        {keyEditable && (
          <p className="text-xs text-muted-foreground">{t("admin.categories.keyHint")}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label>{t("admin.categories.fieldNameEt")}</Label>
          <Input value={value.nameEt} onChange={(e) => onChange({ nameEt: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>{t("admin.categories.fieldNameEn")}</Label>
          <Input value={value.nameEn} onChange={(e) => onChange({ nameEn: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>{t("admin.categories.fieldNameRu")}</Label>
          <Input value={value.nameRu} onChange={(e) => onChange({ nameRu: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t("admin.categories.fieldIcon")}</Label>
          <Select value={value.icon} onValueChange={(v) => onChange({ icon: v })}>
            <SelectTrigger>
              {/* SelectValue renders the chosen SelectItem's content (icon + name) itself. */}
              <SelectValue placeholder={t("admin.categories.fieldIcon")} />
            </SelectTrigger>
            <SelectContent>
              {ICON_NAMES.map((name) => {
                const Icon = getServiceIcon(name);
                return (
                  <SelectItem key={name} value={name}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{name}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>{t("admin.categories.fieldParent")}</Label>
          <Select value={value.parentId} onValueChange={(v) => onChange({ parentId: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PARENT}>{t("admin.categories.parentNone")}</SelectItem>
              {parentOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {getCategoryName(c, i18n.language, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t("admin.categories.fieldSortOrder")}</Label>
        <Input
          type="number"
          value={value.sortOrder}
          onChange={(e) => onChange({ sortOrder: e.target.value })}
          className="max-w-[140px]"
        />
      </div>
    </div>
  );
}
